import axios from "axios";
import { uniqueId } from "lodash-es";
import { request } from "@stemscopes/api";
import i18next from "i18next";
import * as utils from "./utils";
import * as types from "./types";

export const validateFiles: types.validateFiles = ({
  files,
  allowedFilesExtensions,
  maxFileSize,
  maxElements,
  filesCount,
}) => {
  let error = null;

  if (files.length + filesCount > maxElements) {
    error = i18next.t("fileUploader.maxElementsError", { maxElements });
  }

  Array.from(files).forEach((file) => {
    if (allowedFilesExtensions) {
      const fileExtension = utils.getExtension(file.name) || "";
      if (!allowedFilesExtensions.includes(fileExtension)) {
        error = i18next.t("fileUploader.invalidExtensionError", {
          extensions: allowedFilesExtensions.join(", "),
        });
      }
    }

    if (maxFileSize && file.size > utils.getBytesFromMb(maxFileSize)) {
      error = i18next.t("fileUploader.maxFileSizeError", { maxFileSize });
    }
  });

  return error;
};

export const uploadFiles: types.uploadFiles = async ({
  files,
  setLoadingFiles,
  endpoint,
}) => {
  Array.from(files).forEach((file) => {
    const cancelToken = axios.CancelToken.source();
    const tempId = uniqueId();
    const { size, type, name } = file;

    const loadingFile = {
      size,
      name,
      type,
      cancelToken,
      tempId,
      loadingPercentage: 0,
    };

    setLoadingFiles((prev) => [...prev, loadingFile]);

    const updateFileByTempId: types.updateFileByTempId = (newData) => {
      setLoadingFiles((prev) =>
        prev.map((item) => {
          if (item.tempId === tempId) {
            return {
              ...item,
              ...newData,
            };
          }

          return item;
        }),
      );
    };

    const updateLoadingProgressForFile: types.updateLoadingProgressForFile = ({
      loadingPercentage,
    }) => {
      updateFileByTempId({ loadingPercentage });
    };

    const handleResponseForFile: types.handleResponseForFile = ({
      url,
      reference,
    }) => {
      updateFileByTempId({ reference, url });
    };

    const handleErrorResponseForFile: types.handleErrorResponseForFile = (
      error,
    ) => {
      // eslint-disable-next-line
      const errorMessage = error?.data?.errors?._error;
      updateFileByTempId({
        errorMessage:
          errorMessage && errorMessage.length
            ? errorMessage.join(". ")
            : i18next.t("fileUploader.defaultError"),
      });
    };

    const data = new FormData();
    data.append("fileFieldName", file);

    request
      .post(endpoint, data, {
        baseURL: process.env.REACT_APP_ALISYSTEMS_URL,
        cancelToken: cancelToken.token,
        onUploadProgress: (loadingProgress) => {
          const { loaded, total } = loadingProgress;
          if (loaded && total) {
            const loadingPercentage = Number(
              ((loaded / total) * 100).toFixed(0),
            );

            updateLoadingProgressForFile({ loadingPercentage });
          }
        },
      })
      .then((resp) => {
        const { reference, url } = resp.data.fileFieldName;
        handleResponseForFile({ reference, url });
      })
      .catch((error) => {
        handleErrorResponseForFile(error);
      });
  });
};
